import React from "react";
import InputField from "../../SharedComponent/Fields/InputField";
import { useTranslation } from "react-i18next";
import SelectField from "../../SharedComponent/Fields/SelectField";
import TextAreaFielD from "../../SharedComponent/Fields/TextareaField";
import { ImagePlus, Upload, X } from "lucide-react";
import { getAttachmentLargeUrl, getAttachmentThumbUrl, getAttachmentDocumentUrl } from "../../utils/attachmentImages";

const LiftMasts = ({
  formatAttachment,
  handleDeleteAttachment,
  assetAttachments,
  newAsset,
  handleChange,
  warehouses,
  selectedWarehouse,
  handleWarehouseChange,
  warehouseLocationId,
  selectedFiles,
  handleRemoveFile,
  handleFileChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Basics */}
      <div className="bg-white p-4 shadow-md">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.basics")}
        </h2>

        {/* Row 1: Classification, Manufacturer, Type, Serial No */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <SelectField
            name="mast_type"
            value={newAsset.mast_type}
            onChange={handleChange}
            label={t("assets.fields.classification")}
            explanation={t("assets.fields.explanation.classification")}
            placeholder={t("assets.fields.selectClassification")}
            required
            menus={[
              { value: 0, option: t("assets.fields.selectClassification") },
              { value: 1, option: t("assetDetails.mastTypes.standard") },
              { value: 2, option: t("assetDetails.mastTypes.mono") },
              { value: 3, option: t("assetDetails.mastTypes.duplex") },
              { value: 5, option: t("assetDetails.mastTypes.triplex") },
              { value: 8, option: t("assetDetails.mastTypes.quattro") },
              { value: 9, option: t("assetDetails.mastTypes.telescopic") },
            ]}
            selectClassName={
              !newAsset.mast_type
                ? "text-gray-400 font-light"
                : "text-gray-900 font-normal"
            }
          />

          <InputField
            name="manufacturer"
            required
            value={newAsset.manufacturer}
            onChange={handleChange}
            maxLength={100}
            label={t("assets.fields.manufacturer")}
            explanation={t("assets.fields.explanation.manufacturer")}
            placeholder={t("createAsset.placeholder.enterManufacturer")}
          />

          <InputField
            name="type"
            value={newAsset.type}
            onChange={handleChange}
            label={t("assets.fields.type")}
            placeholder={t("createAsset.placeholder.enterType")}
            explanation={t("assets.fields.explanation.type")}
            maxLength={50}
          />

          <InputField
            name="serial_no"
            value={newAsset.serial_no}
            onChange={handleChange}
            placeholder={t("createAsset.placeholder.enterSerialNumber")}
            explanation={t("assets.fields.serialNumber")}
            label={t("assets.fields.machineSerialNo")}
            maxLength={100}
          />
        </div>

        {/* Row 2: Internal No, Status, Capacity, Lift Height */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <InputField
            name="mast_internalno"
            value={newAsset.mast_internalno}
            onChange={handleChange}
            label={t("createAsset.placeholders.internNoLabel")}
            explanation={t("createAsset.placeholders.internNoExplanation")}
            placeholder={t("createAsset.placeholders.internNoPlaceholder")}
            maxLength={100}
          />

          <SelectField
            name="status"
            value={newAsset.status}
            onChange={handleChange}
            label={t("assets.fields.status")}
            placeholder={t("createAsset.placeholder.enterStatus")}
            explanation={t("assets.fields.explanation.status")}
            menus={[
              { value: "", option: t("assets.fields.selectStatus") },
              { value: "New", option: t("assets.options.new") },
              { value: "Used", option: t("assets.options.used") },
              { value: "Demo", option: t("assets.options.demo") },
            ]}
          />

          <InputField
            name="capacity"
            type="number"
            value={newAsset.capacity}
            onChange={handleChange}
            label={t("attachments.fields.capacity")}
            explanation={t("attachments.explanations.capacity")}
            placeholder={t("attachments.placeholders.capacity")}
          />

          <InputField
            name="mast_lift_height"
            type="number"
            value={newAsset.mast_lift_height}
            onChange={handleChange}
            label={t("createAsset.placeholders.liftHeight")}
            explanation={t("createAsset.placeholders.liftHeightExplanation")}
            placeholder={t("createAsset.placeholders.liftHeightPlaceholder")}
          />
          {/* <InputField
            name="mast_commision"
            value={newAsset.mast_commision || ""}
            onChange={handleChange}
            label={t("attachments.fields.commission")}
            type="date"
            placeholder={t("attachments.placeholders.commission")}
            explanation={t("attachments.explanations.commission")}
          /> */}
        </div>

        {/* Row 3: Closed Height, Free Lift, Fork Carriage Width, Hydraulics */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <InputField
            name="mast_closedheight"
            type="number"
            value={newAsset.mast_closedheight}
            onChange={handleChange}
            label={t("createAsset.placeholders.closedHeight")}
            explanation={t("createAsset.placeholders.closedHeightExplanation")}
            placeholder={t("createAsset.placeholders.closedHeightPlaceholder")}
          />

          <InputField
            name="mast_freelift"
            type="number"
            value={newAsset.mast_freelift}
            onChange={handleChange}
            label={t("createAsset.placeholders.freeHeight")}
            placeholder={t("createAsset.placeholders.freeHeightPlaceholder")}
            explanation={t("createAsset.placeholders.freeHeightExplanation")}
          />

          <InputField
            name="mast_fork_carriage_width"
            type="number"
            value={newAsset.mast_fork_carriage_width}
            onChange={handleChange}
            label={t("createAsset.placeholders.forkCarriageLabel")}
            placeholder={t("createAsset.placeholders.forkCarriagePlaceholder")}
            explanation={t("createAsset.placeholders.forkCarriageExplanation")}
          />

          <InputField
            name="mast_additional_hydraulics"
            value={newAsset.mast_additional_hydraulics}
            onChange={handleChange}
            label={t("createAsset.placeholders.additionalHydraulicsLabel")}
            placeholder={t(
              "createAsset.placeholders.additionalHydraulicsPlaceholder"
            )}
            explanation={t(
              "createAsset.placeholders.additionalHydraulicsExplanation"
            )}
            maxLength={100}
          />
          <InputField
            name="commission"
            value={newAsset.commission || ""}
            onChange={handleChange}
            label={t("attachments.fields.commission")}
            type="text"
            placeholder={t("attachments.placeholders.commission")}
            explanation={t("attachments.explanations.commission")}
          />
        </div>

        {/* Row 4: Commission, Condition, Available From */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          {/* <InputField
            name="liftmast_commission"
            value={newAsset.liftmast_commission}
            onChange={handleChange}
            label="Commission"
          /> */}
          <SelectField
            name="optical_condition"
            value={newAsset.optical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.opticalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.opticalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.opticalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.opticalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.technicalCondition.very_good"),
              },
              {
                value: 3,
                option: t("assets.technicalCondition.good"),
              },
              {
                value: 4,
                option: t("assets.technicalCondition.used"),
              },
              {
                value: 5,
                option: t("assets.technicalCondition.bad"),
              },
              {
                value: 6,
                option: t("assets.technicalCondition.very_bad"),
              },
              {
                value: 7,
                option: t("assets.technicalCondition.new"),
              },
            ]}
          />

          <SelectField
            name="technical_condition"
            value={newAsset.technical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.technicalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.technicalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.technicalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.technicalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.technicalCondition.very_good"),
              },
              {
                value: 3,
                option: t("assets.technicalCondition.good"),
              },
              {
                value: 4,
                option: t("assets.technicalCondition.used"),
              },
              {
                value: 5,
                option: t("assets.technicalCondition.bad"),
              },
              {
                value: 6,
                option: t("assets.technicalCondition.very_bad"),
              },
              {
                value: 7,
                option: t("assets.technicalCondition.new"),
              },
            ]}
          />

          <InputField
            name="year_of_manufacture"
            type="date"
            value={newAsset.year_of_manufacture}
            onChange={handleChange}
            label={t("attachments.fields.availableFrom")}
            placeholder={t("attachments.placeholders.availableFrom")}
            explanation={t("attachments.explanations.availableFrom")}
          />
          <div>
            {/* <InputField
              required
              name="year_of_manufacture"
              value={newAsset.year_of_manufacture || ""}
              onChange={handleChange}
              label={t("assets.fields.yearOfManufacture")}
              explanation={t("assets.fields.explanation.yearOfManufacture")}
              placeholder={t("createAsset.placeholder.enterYearOfManufacture")}
              type="month"
              maxLength={100}
            /> */}
          </div>
        </div>

        {/* Row 5: Warehouse + Location */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          {Array.isArray(warehouses) && warehouses?.length > 0 && (
            // <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <SelectField
              name="warehouse"
              value={selectedWarehouse?.id || warehouseLocationId || ""}
              onChange={handleWarehouseChange}
              label="Warehouse"
              menus={[
                { value: "", option: "Select Warehouse" },
                ...warehouses.map((w) => ({ value: w.id, option: w.name })),
              ]}
            />
            // </div>
          )}

          <div>
            <InputField
              required
              name="dealer_street"
              value={newAsset.dealer_street}
              onChange={handleChange}
              maxLength={20}
              label={t("createAsset.placeholders.street")}
              placeholder={t("createAsset.placeholders.streetPlaceholder")}
              explanation={t("createAsset.placeholders.streetExplanation")}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_city"
              value={newAsset.dealer_city}
              onChange={handleChange}
              label={t("createAsset.placeholders.city")}
              explanation={t("createAsset.placeholders.cityExplanation")}
              placeholder={t("createAsset.placeholders.cityPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_country"
              value={newAsset.dealer_country}
              onChange={handleChange}
              label={t("createAsset.placeholders.country")}
              explanation={t("createAsset.placeholders.countryExplanation")}
              placeholder={t("createAsset.placeholders.countryPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_zip"
              value={newAsset.dealer_zip}
              onChange={handleChange}
              type="number"
              label={t("createAsset.placeholders.zipCode")}
              explanation={t("createAsset.placeholders.zipCodePlaceholder")}
              placeholder={t("createAsset.placeholders.zipCodeExplanation")}
              maxLength={7}
            />
          </div>
        </div>

        {/* Row 6: Additional Description */}
        <div className="mt-2">
          <TextAreaFielD
            name="description"
            value={newAsset.description}
            onChange={handleChange}
            label={t("createAsset.placeholders.additionalDescription")}
            placeholder={t(
              "createAsset.placeholders.additionalDescriptionPlaceholder"
            )}
          />
        </div>
      </div>

      {/* Media Section */}
      <div className="bg-white p-4 shadow-md mt-4">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.media")}
        </h2>

        {/* Selected Files */}
        {selectedFiles?.length > 0 && (
          <div className="space-y-2 mt-2">
            <h6>Selected Media</h6>
            <ul className="space-y-1 flex flex-col gap-3">
              {Array.from(selectedFiles).map((file, index) => (
                <li key={index} className="flex items-center gap-4">
                  {file.type.startsWith("image/") && (
                    <img
                      className="h-10 w-10"
                      alt={`mast ${index}`}
                      src={URL.createObjectURL(file)}
                    />
                  )}
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm font-semibold underline ml-2"
                  >
                    {file.name}
                  </a>
                  <div
                    onClick={() => handleRemoveFile(index)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploaded Media */}
        {assetAttachments?.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            <h6>Uploaded Media</h6>
            <div className="flex flex-col gap-3">
              {assetAttachments.map((attachment, index) => (
                <div key={index} className="flex items-center gap-2">
                  {attachment?.image ? (
                    <>
                      <img
                        className="h-10 w-10"
                        src={getAttachmentThumbUrl(attachment)}
                        alt="mast"
                      />
                      <a
                        href={getAttachmentLargeUrl(attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-semibold underline ml-2"
                      >
                        {formatAttachment(getAttachmentLargeUrl(attachment))}
                      </a>
                    </>
                  ) : (
                    <a
                      href={getAttachmentDocumentUrl(attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm font-semibold underline ml-2"
                    >
                      {formatAttachment(getAttachmentDocumentUrl(attachment))}
                    </a>
                  )}
                  <div
                    onClick={() => handleDeleteAttachment(attachment?.id)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="my-4">
          <div className="relative">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept="image/*, .xlsx, .xls, .doc, .docx, .pdf"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              id="liftmast-upload"
            />
            <label
              htmlFor="liftmast-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                </div>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">
                    {t("assetPageHeadings.clickToUpload")}
                  </span>
                </p>
                <p className="text-xs text-gray-400">
                  {t("assetPageHeadings.fileSupported")}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default LiftMasts;
